"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { ImageAttachment } from "@bash/shared";
import {
  ATTACHMENT_BYTE_PATH_MAX_BYTES,
  UPLOAD_MAX_BYTES,
  mediaKindFor,
  mediaMimeFromFilename,
} from "@bash/shared";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { getFileIcon } from "@/lib/file-icons";
import { resilientFetch } from "@/lib/fetch";
import { attachmentDoor } from "@/lib/files-urls";
import type { SharedFileRef } from "@/stores/composerDraftStore";
import { putWithProgress } from "./putWithProgress";
import { ERROR_INK, ERROR_TINT, UploadRing } from "./UploadRing";
import {
  isStorageTooLarge,
  storageRefusedMessage,
  tooLargeMessage,
  tooManyFiles,
  uploadPercent,
} from "./uploadCopy";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

export type ImageMediaType =
  "image/jpeg" | "image/png" | "image/gif" | "image/webp";

export interface PendingAttachment {
  id: string;
  filename: string;
  sizeBytes: number;
  mediaType: string;
  isImage: boolean;
  status: "processing" | "complete" | "error";
  /** For images: object URL for instant preview */
  thumbnailUrl?: string;
  /** Completed image data */
  imageData?: { data: string; mediaType: ImageMediaType };
  /** Completed file data */
  fileData?: { url: string; mediaType: string; sizeBytes: number };
  /** Upload progress, 0..1, while the bytes go up (a non-image file only). */
  progress?: number;
  error?: string;
  /** Storage refused the PUT for its size (ruling I1): the file card draws the
   *  name over the no-number advice instead of the one-line `error`. */
  storageRefused?: true;
}

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const TARGET_SIZE = 1 * 1024 * 1024;
const MAX_DIMENSION = 2048;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

async function compressImage(
  file: File,
  maxDimension: number = MAX_DIMENSION,
  targetSize: number = TARGET_SIZE,
  quality: number = 0.85
): Promise<{
  data: string;
  mediaType: "image/jpeg" | "image/png" | "image/gif" | "image/webp";
}> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      if (file.type === "image/gif") {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64Data = result.split(",")[1];
          resolve({ data: base64Data, mediaType: "image/gif" });
        };
        reader.onerror = () => reject(new Error("Failed to read GIF"));
        reader.readAsDataURL(file);
        return;
      }

      const hasTransparency =
        file.type === "image/png" || file.type === "image/webp";
      let outputType: "image/jpeg" | "image/png" = hasTransparency
        ? "image/png"
        : "image/jpeg";
      let currentQuality = quality;

      const tryCompress = (): string => {
        const dataUrl = canvas.toDataURL(outputType, currentQuality);
        const base64Size = dataUrl.length * 0.75;

        if (
          base64Size > targetSize &&
          outputType === "image/jpeg" &&
          currentQuality > 0.5
        ) {
          currentQuality -= 0.1;
          return tryCompress();
        }

        if (base64Size > targetSize && outputType === "image/png") {
          outputType = "image/jpeg";
          currentQuality = 0.85;
          return tryCompress();
        }

        return dataUrl;
      };

      const dataUrl = tryCompress();
      const base64Data = dataUrl.split(",")[1];
      resolve({ data: base64Data, mediaType: outputType });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
}

/**
 * "video" / "audio" for a file the box takes BY REFERENCE, else null.
 *
 * The MIME the browser reports is a guess and is routinely empty for a
 * container it does not know, so the extension is the second opinion, read
 * from the SAME table the server routes on (`MEDIA_MIME_BY_EXT` in
 * @bash/shared). A PDF is media to `mediaKindFor` but not here: the server
 * sends it by bytes.
 */
function mediaKind(file: File): "video" | "audio" | null {
  const kind =
    mediaKindFor(file.type) ?? mediaKindFor(mediaMimeFromFilename(file.name));
  return kind === "video" || kind === "audio" ? kind : null;
}

/**
 * The wall a non-inline file meets on its way to the box (final review I2):
 * video and audio go by reference, bounded by the upload cap (500 MB);
 * everything else, images sent as files included, is downloaded whole by the
 * server, walled at the byte path's 50 MB. Refused here, the person loses
 * nothing; past this, the server refused it after the whole upload.
 */
function uploadLimitFor(file: File): number {
  return mediaKind(file) ? UPLOAD_MAX_BYTES : ATTACHMENT_BYTE_PATH_MAX_BYTES;
}

/** A referenced file's row id: one row per file, whoever hands it over. */
export const referencedRowId = (fileId: string): string => `ref-${fileId}`;

/**
 * Hook that provides file selection, image compression, S3 upload, and attachment state management.
 *
 * `imagesAsFiles` (spec 2026-09-24, the ask card's file answer): an image takes
 * the upload path like any file instead of compressing to inline pixels. An
 * answer reaches the box by url, and inline pixels have none.
 *
 * `maxFiles` (the ask card, final review M1): the most rows the lane holds. A
 * pick past the room left stages the first files that fit, never the rest,
 * and says so (`tooMany`, and the one-line note). Absent, every file stages.
 */
export function useAttachmentHandler({
  imagesAsFiles = false,
  maxFiles,
}: { imagesAsFiles?: boolean; maxFiles?: number } = {}) {
  const { getAccessToken } = useAuth();
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const [imageError, setImageError] = useState<string | null>(null);
  // The last pick refused for its size, as a fact: the file card draws it as
  // its own error plate ("<name> is 1.7 GB" over the advice for its limit).
  const [refusal, setRefusal] = useState<{
    filename: string;
    sizeBytes: number;
    limitBytes: number;
  } | null>(null);
  // The last pick overflowed `maxFiles`: its overflow was never staged.
  const [tooMany, setTooMany] = useState(false);
  // The rows as last drawn, for a pick's room under `maxFiles`: a pick
  // starts from a click, after the render that drew them.
  const rowsRef = useRef(pendingAttachments);
  useEffect(() => {
    rowsRef.current = pendingAttachments;
  }, [pendingAttachments]);
  // One cancel per uploading non-image row, for THIS hook only (ruling I3):
  // removing a row mid-upload must stop its PUT, or the file lands in Files.
  const inflight = useRef(new Map<string, AbortController>());

  const handleImageSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;

      setImageError(null);
      setRefusal(null);
      setTooMany(false);

      let picked = Array.from(files);
      if (maxFiles !== undefined) {
        const room = Math.max(0, maxFiles - rowsRef.current.length);
        if (picked.length > room) {
          picked = picked.slice(0, room);
          setTooMany(true);
          setImageError(tooManyFiles(maxFiles));
        }
      }

      for (const file of picked) {
        const isImage = !imagesAsFiles && ALLOWED_TYPES.includes(file.type);
        const attachId = `att-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 6)}`;

        if (isImage) {
          if (file.size > MAX_IMAGE_SIZE) {
            setImageError(
              `File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB`
            );
            continue;
          }

          const thumbnailUrl = URL.createObjectURL(file);
          setPendingAttachments((prev) => [
            ...prev,
            {
              id: attachId,
              filename: file.name,
              sizeBytes: file.size,
              mediaType: file.type,
              isImage: true,
              status: "processing",
              thumbnailUrl,
            },
          ]);

          try {
            const compressed = await compressImage(file);
            setPendingAttachments((prev) =>
              prev.map((a) =>
                a.id === attachId
                  ? {
                      ...a,
                      status: "complete" as const,
                      imageData: {
                        data: compressed.data,
                        mediaType: compressed.mediaType,
                      },
                    }
                  : a
              )
            );
          } catch (error) {
            console.error("Image compression failed:", error);
            setPendingAttachments((prev) =>
              prev.map((a) =>
                a.id === attachId
                  ? {
                      ...a,
                      status: "error" as const,
                      error: "Failed to process image",
                    }
                  : a
              )
            );
          }
        } else {
          // Every non-inline file is checked against the wall it will meet
          // (spec 2026-09-24; final review I2). Refusing here costs the person
          // nothing; finding out mid-PUT, or after it, costs them the upload.
          const limitBytes = uploadLimitFor(file);
          if (file.size > limitBytes) {
            setImageError(tooLargeMessage(file.name, file.size, limitBytes));
            setRefusal({
              filename: file.name,
              sizeBytes: file.size,
              limitBytes,
            });
            continue;
          }

          const cancel = new AbortController();
          inflight.current.set(attachId, cancel);
          let storageRefused = false;
          setPendingAttachments((prev) => [
            ...prev,
            {
              id: attachId,
              filename: file.name,
              sizeBytes: file.size,
              mediaType: file.type || "application/octet-stream",
              isImage: false,
              status: "processing",
            },
          ]);

          try {
            const token = await getAccessToken();
            const headers: Record<string, string> = {
              "Content-Type": "application/json",
            };
            if (token) headers["Authorization"] = `Bearer ${token}`;

            const urlRes = await resilientFetch(
              `${SERVER_URL}/api/files/upload-url`,
              {
                method: "POST",
                headers,
                body: JSON.stringify({
                  filename: file.name,
                  bucket: "uploads",
                }),
              }
            );
            if (!urlRes.ok) throw new Error("Failed to get upload URL");
            const { signedUrl, storagePath } = await urlRes.json();

            // XHR, not fetch: fetch cannot report upload progress, and a
            // 500 MB upload takes minutes. The chip shows a whole percent.
            let shownPercent = -1;
            const put = await putWithProgress(
              signedUrl,
              file,
              file.type || "application/octet-stream",
              (fraction) => {
                const percent = uploadPercent(fraction);
                if (percent === shownPercent) return;
                shownPercent = percent;
                setPendingAttachments((prev) =>
                  prev.map((a) =>
                    a.id === attachId ? { ...a, progress: fraction } : a
                  )
                );
              },
              cancel.signal
            );
            // Storage refuses an oversize object with HTTP 400 and the 413
            // INSIDE its JSON body (prod 2026-09-24), read as fields. Unread,
            // it surfaced as "Failed to confirm upload" a minute later. The
            // words name no limit (ruling I1): Storage's cap is a dashboard
            // setting that may lag ours.
            if (put.status < 200 || put.status >= 300) {
              storageRefused = isStorageTooLarge(put.status, put.body);
              throw new Error(
                storageRefused
                  ? storageRefusedMessage(file.name)
                  : "Upload failed"
              );
            }

            const confirmRes = await resilientFetch(
              `${SERVER_URL}/api/files/confirm-upload`,
              {
                method: "POST",
                headers,
                body: JSON.stringify({
                  storagePath,
                  filename: file.name,
                  mimeType: file.type || null,
                  sizeBytes: file.size,
                  bucket: "uploads",
                }),
                // A removal while the confirm is in flight cancels it too.
                signal: cancel.signal,
              }
            );
            if (!confirmRes.ok) throw new Error("Failed to confirm upload");
            const result = await confirmRes.json();
            // The upload rides by its door: the server reads the id in its path
            // and asks the gate. The confirm always names the file it stored
            // (file-routes.handler.ts); the 60-second read pass it also mints
            // expires. Only a confirm that names no file falls back to that
            // pass. The upload's signedUrl is a WRITE token and is never sent:
            // a confirm with neither is a failed confirm (an error row is never
            // sent, and a complete row with no url would be refused whole by
            // the task socket's FileAttachmentSchema).
            const url: string | undefined = result.fileId
              ? attachmentDoor(result.fileId)
              : result.downloadUrl;
            if (!url) throw new Error("Failed to confirm upload");

            setPendingAttachments((prev) =>
              prev.map((a) =>
                a.id === attachId
                  ? {
                      ...a,
                      status: "complete" as const,
                      fileData: {
                        url,
                        mediaType: file.type || "application/octet-stream",
                        sizeBytes: file.size,
                      },
                    }
                  : a
              )
            );
          } catch (err) {
            // A cancel is the person removing the row: it is already gone.
            // Read from the row's own signal, not the error's shape: the PUT
            // rejects with its own AbortError, and a cancelled confirm with
            // whatever resilientFetch rethrows for a caller abort.
            if (!cancel.signal.aborted) {
              setPendingAttachments((prev) =>
                prev.map((a) =>
                  a.id === attachId
                    ? {
                        ...a,
                        status: "error" as const,
                        error:
                          err instanceof Error ? err.message : "Upload failed",
                        ...(storageRefused
                          ? { storageRefused: true as const }
                          : {}),
                      }
                    : a
                )
              );
            }
          } finally {
            inflight.current.delete(attachId);
          }
        }
      }
    },
    [getAccessToken, imagesAsFiles, maxFiles]
  );

  const openFilePicker = useCallback(
    (handleSelect: (e: React.ChangeEvent<HTMLInputElement>) => void) => {
      const input = document.createElement("input");
      input.type = "file";
      // No `accept` filter: the upload pipeline is type-agnostic and the other
      // entry points (drag-drop, paste, Files page) take any file. A curated
      // list here silently greys out valid files in the OS dialog (.html was
      // unselectable while the same file dropped onto the composer fine).
      input.multiple = true;
      // Off-screen, never display:none (some engines refuse clicks on
      // display:none file inputs — why this is a temp element at all).
      input.style.position = "fixed";
      input.style.left = "-9999px";
      input.style.top = "0";
      input.style.opacity = "0";
      input.style.pointerEvents = "none";
      input.onchange = (e) => {
        handleSelect(e as unknown as React.ChangeEvent<HTMLInputElement>);
        input.remove();
      };
      // Cancel-leak fix (07-09, desktop shell): dismissing the picker never fired
      // onchange, so the raw "Choose Files no files selected" input stayed at the
      // body bottom forever.
      input.oncancel = () => input.remove();
      document.body.appendChild(input);
      // The browser hands focus back to the element that had it when the
      // dialog closes. That was the + button, and its tooltip opens on focus,
      // so "Attach file" stood over the chips with the mouse nowhere near it
      // (Adarsh, #bugs 09-23). So the trigger lets go of it first. Only a
      // button: WebKit (Safari, the desktop shell) and Firefox on macOS never
      // focus a button on click, and there the focus is the editor's to keep.
      if (document.activeElement instanceof HTMLButtonElement) {
        document.activeElement.blur();
      }
      try {
        input.click();
      } catch {
        input.remove();
      }
    },
    []
  );

  const removeAttachment = useCallback((id: string) => {
    inflight.current.get(id)?.abort();
    inflight.current.delete(id);
    setPendingAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att?.thumbnailUrl) URL.revokeObjectURL(att.thumbnailUrl);
      return prev.filter((a) => a.id !== id);
    });
    setImageError(null);
    setRefusal(null);
    setTooMany(false);
  }, []);

  const resetAttachments = useCallback(() => {
    for (const cancel of inflight.current.values()) cancel.abort();
    inflight.current.clear();
    setPendingAttachments([]);
    setImageError(null);
    setRefusal(null);
    setTooMany(false);
  }, []);

  const dismissRefusal = useCallback(() => {
    setRefusal(null);
    setImageError(null);
    setTooMany(false);
  }, []);

  const restoreImages = useCallback((images: ImageAttachment[]) => {
    setPendingAttachments(
      images.map((img, i) => ({
        id: `restored-img-${i}`,
        filename: `image-${i + 1}.${img.mediaType.split("/")[1]}`,
        sizeBytes: 0,
        mediaType: img.mediaType,
        isImage: true,
        status: "complete" as const,
        imageData: {
          data: img.data,
          mediaType: img.mediaType as ImageMediaType,
        },
      }))
    );
  }, []);

  /** Process raw File objects (from drag-and-drop or paste). Reuses the same
   *  image compression / S3 upload pipeline as the file picker. */
  const handleFiles = useCallback(
    (files: File[]) => {
      // Synthesize a fake ChangeEvent with the files so we reuse handleImageSelect
      const dt = new DataTransfer();
      for (const f of files) dt.items.add(f);
      const fakeInput = document.createElement("input");
      fakeInput.type = "file";
      fakeInput.files = dt.files;
      const fakeEvent = {
        target: fakeInput,
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleImageSelect(fakeEvent);
    },
    [handleImageSelect]
  );

  /** Files already stored (shared with them): a finished row that rides by its /v door, no upload.
   *  The server reads the door as the file id and asks the gate (theirs, or shared with them).
   *  One row per file, so a double click adds one; the cap holds the way a pick's does. */
  const addReferencedFiles = useCallback(
    (refs: SharedFileRef[]) => {
      setImageError(null);
      setRefusal(null);
      setTooMany(false);
      let fresh = refs.filter(
        (r, i) =>
          refs.findIndex((o) => o.fileId === r.fileId) === i &&
          !rowsRef.current.some((a) => a.id === referencedRowId(r.fileId))
      );
      if (maxFiles !== undefined) {
        const room = Math.max(0, maxFiles - rowsRef.current.length);
        if (fresh.length > room) {
          fresh = fresh.slice(0, room);
          setTooMany(true);
          setImageError(tooManyFiles(maxFiles));
        }
      }
      setPendingAttachments((prev) => [
        ...prev,
        ...fresh
          .filter((r) => !prev.some((a) => a.id === referencedRowId(r.fileId)))
          .map((r) => ({
            id: referencedRowId(r.fileId),
            filename: r.filename,
            sizeBytes: r.sizeBytes,
            mediaType: r.mediaType,
            isImage: false,
            status: "complete" as const,
            fileData: {
              url: attachmentDoor(r.fileId),
              mediaType: r.mediaType,
              sizeBytes: r.sizeBytes,
            },
          })),
      ]);
    },
    [maxFiles]
  );

  return {
    pendingAttachments,
    imageError,
    refusal,
    tooMany,
    dismissRefusal,
    setImageError,
    handleImageSelect,
    handleFiles,
    addReferencedFiles,
    openFilePicker,
    removeAttachment,
    resetAttachments,
    restoreImages,
  };
}

/** A non-image row's chip (the mock's `.chip`): a ring and a whole percent
 *  while its bytes go up, a warning when they failed, else the file's icon. */
function FileChip({
  att,
  filenameMaxWidth,
}: {
  att: PendingAttachment;
  filenameMaxWidth: number;
}) {
  const uploading = att.status === "processing";
  const pct =
    uploading && att.progress !== undefined
      ? uploadPercent(att.progress)
      : null;
  return (
    <div
      className={cn(
        "flex h-8 items-center gap-2 rounded-12 px-3 text-[13.5px]",
        att.status === "error"
          ? `${ERROR_TINT} ${ERROR_INK}`
          : "bg-bg-muted text-fg-muted"
      )}
      // Uploading, the chip is a progress bar a reader can query; the value
      // moves with the percent, and no live region speaks every tick.
      {...(uploading
        ? {
            role: "progressbar",
            "aria-label": `Uploading ${att.filename}`,
            "aria-valuemin": 0,
            "aria-valuemax": 100,
            "aria-valuenow": pct ?? undefined,
          }
        : {})}
    >
      {pct !== null ? (
        <UploadRing progress={att.progress ?? 0} />
      ) : uploading ? (
        <span
          className="w-3 h-3 border-2 border-fg-muted border-t-transparent rounded-full animate-spin-1 flex-shrink-0"
          data-testid="upload-spinner"
        />
      ) : att.status === "error" ? (
        <span className="flex-shrink-0" aria-hidden="true">
          ⚠
        </span>
      ) : (
        <span className="flex-shrink-0">{getFileIcon(att.filename, 14)}</span>
      )}
      <span className="truncate" style={{ maxWidth: filenameMaxWidth }}>
        {att.filename}
      </span>
      {att.status === "error" && <span className="sr-only"> failed</span>}
      {pct !== null && (
        <span className="min-w-[4ch] flex-shrink-0 text-right font-medium text-fg-default tabular-nums">
          {pct}%
        </span>
      )}
    </div>
  );
}

/** Renders the pending attachments strip (images + files) */
export function AttachmentStrip({
  pendingAttachments,
  imageError,
  onRemove,
  filenameMaxWidth = 120,
  className,
  canRemove,
}: {
  pendingAttachments: PendingAttachment[];
  imageError: string | null;
  onRemove: (id: string) => void;
  /** Max px width of the truncated filename. Wider on roomy surfaces (home dock). */
  filenameMaxWidth?: number;
  /** Merged over the row's own classes (the Line's card drops the rule and padding). */
  className?: string;
  /** Whether a row still takes its ×; every row does when absent. The
   *  Line's card keeps a file it already handed to the call until it is taken. */
  canRemove?: (att: PendingAttachment) => boolean;
}) {
  // ONE note under the strip (the mock's): a refused pick first, else the
  // newest failed upload's reason.
  const failed = pendingAttachments.filter(
    (a) => a.status === "error" && a.error
  );
  const note = imageError ?? failed[failed.length - 1]?.error ?? null;
  return (
    <>
      {pendingAttachments.length > 0 && (
        <div
          className={cn(
            "flex flex-wrap gap-2 px-4 py-3 border-b border-border-subtle",
            className
          )}
        >
          {pendingAttachments.map((att) => (
            <div key={att.id} className="relative group">
              {att.isImage ? (
                <div className="relative">
                  <img
                    src={
                      att.imageData
                        ? `data:${att.imageData.mediaType};base64,${att.imageData.data}`
                        : att.thumbnailUrl || ""
                    }
                    alt={att.filename}
                    className={cn(
                      "h-16 w-16 object-cover rounded-12 border border-border-default",
                      att.status === "error" && "opacity-50 border-error"
                    )}
                  />
                  {att.status === "processing" && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-12 bg-black/30">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin-1" />
                    </div>
                  )}
                </div>
              ) : (
                <FileChip att={att} filenameMaxWidth={filenameMaxWidth} />
              )}
              {/* An uploading row takes its × too: removing it cancels the
                  PUT (ruling I3). Shown on hover, on keyboard focus, and
                  always on a screen with no hover (a phone), or a long
                  upload could not be cancelled while its send is held. */}
              {(canRemove?.(att) ?? true) && (
                <button
                  onClick={() => onRemove(att.id)}
                  className={cn(
                    "absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-3.5 font-bold transition-colors flex items-center justify-center bg-status-error text-white",
                    "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
                  )}
                  title="Remove"
                  aria-label={`Remove ${att.filename}`}
                >
                  x
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {note && (
        <div className={cn("px-4 py-2 text-3.5", ERROR_INK)} role="alert">
          {note}
        </div>
      )}
    </>
  );
}
